import React, { useState, useRef, useEffect, useContext } from "react";
import axios from "axios";
import AppContext from "../../../../context/AppContext";
import "./Conversation.css";

const Conversation = () => {
  const { baseUrl } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState("all");
  const [conversations, setConversations] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const messagesEndRef = useRef(null);

  /*
  |--------------------------------------------------------------------------
  | GET ADMIN ID
  |--------------------------------------------------------------------------
  */

  const getAdminId = () => {
    return localStorage.getItem("adminId");
  };

  /*
  |--------------------------------------------------------------------------
  | GET ADMIN TOKEN
  |--------------------------------------------------------------------------
  */

  const getAdminToken = () => {
    return localStorage.getItem("adminToken");
  };

  /*
  |--------------------------------------------------------------------------
  | AXIOS HEADERS
  |--------------------------------------------------------------------------
  */

  const getAuthHeaders = () => {
    const token = getAdminToken();

    return {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    };
  };

  /*
  |--------------------------------------------------------------------------
  | FETCH CONVERSATIONS
  |--------------------------------------------------------------------------
  */

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentAdminId = getAdminId();
      const adminToken = getAdminToken();

      console.log("FETCH ADMIN ID:", currentAdminId);
      console.log(
        "ADMIN TOKEN EXISTS:",
        !!adminToken
      );

      /*
      |--------------------------------------------------------------------------
      | ADMIN ID CHECK
      |--------------------------------------------------------------------------
      */

      if (!currentAdminId) {
        setError(
          "Admin ID not found. Please logout and login again."
        );
        return;
      }

      /*
      |--------------------------------------------------------------------------
      | ADMIN TOKEN CHECK
      |--------------------------------------------------------------------------
      */

      if (!adminToken) {
        setError(
          "Admin session expired. Please login again."
        );
        return;
      }

      const res = await axios.post(
        `${baseUrl}/admin/Conversation/fetchAdminConversations`,
        {
          admin_id: currentAdminId,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      console.log(
        "CONVERSATIONS RESPONSE:",
        res.data
      );

      if (res.data.success) {

        const backendConversations =
          Array.isArray(res.data.conversations)
            ? res.data.conversations
            : [];

        /*
        |--------------------------------------------------------------------------
        | MAP BACKEND DATA TO FRONTEND
        |--------------------------------------------------------------------------
        */

        const mapped = backendConversations.map(
          (conv) => {

            const customer = conv.mlcustomer || {};

            const firstname =
              customer.firstname || "";

            const lastname =
              customer.lastname || "";

            const customerName =
              `${firstname} ${lastname}`.trim() ||
              "Unknown Customer";

            /*
            |--------------------------------------------------------------------------
            | CUSTOMER IMAGE
            |--------------------------------------------------------------------------
            */

            let avatar =
              customer.image ||
              "https://i.pravatar.cc/150";

            /*
            | If backend gives relative storage path
            */

            if (
              customer.image &&
              !customer.image.startsWith("http")
            ) {
              avatar =
                "http://127.0.0.1:8000/storage/" +
                customer.image.replace(
                  /^\/?storage\//,
                  ""
                );
            }

            /*
            |--------------------------------------------------------------------------
            | MAP MESSAGES
            |--------------------------------------------------------------------------
            */

            const messages = Array.isArray(
              conv.mlchats
            )
              ? conv.mlchats.map((m) => ({
                  id: m.id,

                  sender:
                    m.sender || "customer",

                  text:
                    m.message || "",

                  timestamp: m.created_at
                    ? new Date(
                        m.created_at
                      ).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )
                    : "",
                }))
              : [];

            /*
            |--------------------------------------------------------------------------
            | LAST MESSAGE
            |--------------------------------------------------------------------------
            */

            const lastMessage =
              messages.length > 0
                ? messages[
                    messages.length - 1
                  ]
                : null;

            /*
            |--------------------------------------------------------------------------
            | LAST ACTIVITY
            |--------------------------------------------------------------------------
            */

            const lastActivity =
              conv.updated_at
                ? new Date(
                    conv.updated_at
                  ).toLocaleTimeString(
                    "en-US",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )
                : "";

            return {
              id: conv.id,

              customer: {
                name: customerName,

                avatar: avatar,

                location:
                  customer.city ||
                  customer.address ||
                  "",

                online: false,
              },

              messages: messages,

              status:
                conv.status ?? null,

              priority:
                conv.priority ?? null,

              category:
                conv.category ?? null,

              mlorder_id:
                conv.mlorder_id ?? null,

              lastActivity:
                lastActivity,

              preview:
                lastMessage?.text || "",
            };
          }
        );

        setConversations(mapped);

        /*
        |--------------------------------------------------------------------------
        | SELECT FIRST CHAT
        |--------------------------------------------------------------------------
        */

        setSelectedChat((currentSelected) => {

          if (!currentSelected && mapped.length > 0) {
            return mapped[0];
          }

          /*
          | Keep currently selected conversation updated
          */

          if (currentSelected) {

            const updatedSelected =
              mapped.find(
                (conversation) =>
                  conversation.id ===
                  currentSelected.id
              );

            return (
              updatedSelected ||
              currentSelected
            );
          }

          return null;
        });

        setError(null);

      } else {

        setError(
          res.data.message ||
            "Conversations load nahi ho sakein."
        );
      }

    } catch (err) {

      console.error(
        "FETCH CONVERSATIONS ERROR:",
        err.response?.data || err
      );

      if (
        err.response?.status === 401
      ) {
        localStorage.removeItem(
          "adminToken"
        );

        localStorage.removeItem(
          "adminId"
        );

        localStorage.removeItem(
          "adminUser"
        );

        localStorage.removeItem(
          "adminemail"
        );

        setError(
          "Admin session expired. Please login again."
        );

        return;
      }

      setError(
        err.response?.data?.message ||
          "Server se connect nahi ho pa raha."
      );

    } finally {

      setLoading(false);

    }
  };

  /*
  |--------------------------------------------------------------------------
  | INITIAL FETCH
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (baseUrl) {
      fetchConversations();
    }

  }, [baseUrl]);

  /*
  |--------------------------------------------------------------------------
  | SCROLL TO BOTTOM
  |--------------------------------------------------------------------------
  */

  const scrollToBottom = () => {

    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });

  };

  useEffect(() => {

    scrollToBottom();

  }, [selectedChat]);

  /*
  |--------------------------------------------------------------------------
  | SEND MESSAGE
  |--------------------------------------------------------------------------
  */

  const handleSendMessage = async () => {

    if (
      messageInput.trim() === "" ||
      !selectedChat
    ) {
      return;
    }

    const textToSend =
      messageInput.trim();

    setMessageInput("");

    try {

      const adminToken =
        getAdminToken();

      if (!adminToken) {
        alert(
          "Admin session expired. Please login again."
        );

        return;
      }

      const res = await axios.post(
        `${baseUrl}/admin/Conversation/sendMessage`,
        {
          Mlconversation_id:
            selectedChat.id,

          message:
            textToSend,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      console.log(
        "SEND MESSAGE RESPONSE:",
        res.data
      );

      if (res.data.success) {

        const newMessage = {
          id:
            res.data.chat?.id ||
            Date.now(),

          sender: "admin",

          text:
            res.data.chat?.message ||
            textToSend,

          timestamp:
            res.data.chat?.created_at
              ? new Date(
                  res.data.chat.created_at
                ).toLocaleTimeString(
                  "en-US",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )
              : new Date().toLocaleTimeString(
                  "en-US",
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                ),
        };

        /*
        |--------------------------------------------------------------------------
        | UPDATE CONVERSATIONS LIST
        |--------------------------------------------------------------------------
        */

        setConversations(
          (previousConversations) =>
            previousConversations.map(
              (conversation) => {

                if (
                  conversation.id !==
                  selectedChat.id
                ) {
                  return conversation;
                }

                return {
                  ...conversation,

                  messages: [
                    ...conversation.messages,
                    newMessage,
                  ],

                  preview:
                    newMessage.text,

                  lastActivity:
                    newMessage.timestamp,
                };
              }
            )
        );

        /*
        |--------------------------------------------------------------------------
        | UPDATE SELECTED CHAT
        |--------------------------------------------------------------------------
        */

        setSelectedChat(
          (previousChat) => {

            if (!previousChat) {
              return previousChat;
            }

            return {
              ...previousChat,

              messages: [
                ...previousChat.messages,
                newMessage,
              ],

              preview:
                newMessage.text,

              lastActivity:
                newMessage.timestamp,
            };
          }
        );

        /*
        |--------------------------------------------------------------------------
        | SCROLL AFTER MESSAGE
        |--------------------------------------------------------------------------
        */

        setTimeout(() => {
          scrollToBottom();
        }, 100);

      } else {

        setMessageInput(textToSend);

        alert(
          res.data.message ||
            "Message send nahi ho saka."
        );
      }

    } catch (err) {

      console.error(
        "SEND MESSAGE ERROR:",
        err.response?.data || err
      );

      setMessageInput(textToSend);

      alert(
        err.response?.data?.message ||
          "Message send nahi ho saka, dobara try karo."
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | MARK RESOLVED
  |--------------------------------------------------------------------------
  */

  const handleMarkResolved = () => {

    /*
    |--------------------------------------------------------------------------
    | Backend mein abhi resolve endpoint nahi hai.
    |--------------------------------------------------------------------------
    */

    alert(
      'Backend mein "resolved" status/endpoint abhi exist nahi karta.'
    );
  };

  /*
  |--------------------------------------------------------------------------
  | VIEW ORDER
  |--------------------------------------------------------------------------
  */

  const handleViewOrder = () => {

    if (selectedChat?.mlorder_id) {

      window.location.href =
        `/admin/orders/${selectedChat.mlorder_id}`;

    } else {

      alert(
        "Is conversation se koi order linked nahi hai."
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | FILTER CONVERSATIONS
  |--------------------------------------------------------------------------
  */

  const filteredConversations =
    conversations.filter((conv) => {

      const search =
        searchQuery
          .toLowerCase()
          .trim();

      const customerName =
        conv.customer?.name
          ?.toLowerCase() || "";

      const conversationId =
        conv.id?.toString() || "";

      const matchesSearch =
        customerName.includes(search) ||
        conversationId.includes(search);

      const matchesTab =
        activeTab === "all" ||
        (
          activeTab === "human" &&
          conv.status ===
            "human_required"
        ) ||
        (
          activeTab === "bot" &&
          conv.status ===
            "bot_handled"
        );

      return (
        matchesSearch &&
        matchesTab
      );
    });

  /*
  |--------------------------------------------------------------------------
  | STATUS BADGE
  |--------------------------------------------------------------------------
  */

  const getStatusBadge = (status) => {

    switch (status) {

      case "human_required":

        return (
          <span className="AdminConversationAdmin__badge AdminConversationAdmin__badge--warning">
            HUMAN REQUIRED
          </span>
        );

      case "bot_handled":

        return (
          <span className="AdminConversationAdmin__badge AdminConversationAdmin__badge--success">
            BOT HANDLED
          </span>
        );

      case "resolved":

        return (
          <span className="AdminConversationAdmin__badge AdminConversationAdmin__badge--info">
            RESOLVED
          </span>
        );

      default:

        return null;
    }
  };

  /*
  |--------------------------------------------------------------------------
  | PRIORITY BADGE
  |--------------------------------------------------------------------------
  */

  const getPriorityBadge = (
    priority
  ) => {

    if (priority === "urgent") {

      return (
        <span className="AdminConversationAdmin__badge AdminConversationAdmin__badge--danger">
          URGENT
        </span>
      );
    }

    return null;
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {

    return (
      <div className="AdminConversationAdmin__loading">
        Loading conversations...
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  if (error) {

    return (
      <div className="AdminConversationAdmin__error">
        {error}
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | MAIN UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="AdminConversationAdmin">

      <div className="AdminConversationAdmin__main">

        {/* ==============================================================
            SIDEBAR
        ============================================================== */}

        <div className="AdminConversationAdmin__sidebar">

          {/* SEARCH */}

          <div className="AdminConversationAdmin__search">

            <svg
              className="AdminConversationAdmin__search-icon"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM19 19l-4.35-4.35"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <input
              type="text"
              className="AdminConversationAdmin__search-input"
              placeholder="Search customer name or ID..."
              value={searchQuery}
              onChange={(e) =>
                setSearchQuery(
                  e.target.value
                )
              }
            />

          </div>


          {/* TABS */}

          <div className="AdminConversationAdmin__tabs">

            <button
              className={`AdminConversationAdmin__tab ${
                activeTab === "all"
                  ? "AdminConversationAdmin__tab--active"
                  : ""
              }`}
              onClick={() =>
                setActiveTab("all")
              }
            >
              All Chats
            </button>


            <button
              className={`AdminConversationAdmin__tab ${
                activeTab === "human"
                  ? "AdminConversationAdmin__tab--active"
                  : ""
              }`}
              onClick={() =>
                setActiveTab("human")
              }
            >
              Human Needed{" "}

              <span className="AdminConversationAdmin__tab-indicator AdminConversationAdmin__tab-indicator--warning"></span>

            </button>


            <button
              className={`AdminConversationAdmin__tab ${
                activeTab === "bot"
                  ? "AdminConversationAdmin__tab--active"
                  : ""
              }`}
              onClick={() =>
                setActiveTab("bot")
              }
            >
              Bot Handled{" "}

              <span className="AdminConversationAdmin__tab-indicator AdminConversationAdmin__tab-indicator--success"></span>

            </button>

          </div>


          {/* CHAT LIST */}

          <div className="AdminConversationAdmin__chat-list">

            {filteredConversations.length ===
              0 && (

              <div className="AdminConversationAdmin__empty">
                Koi conversation nahi mili.
              </div>

            )}


            {filteredConversations.map(
              (conv) => (

                <div
                  key={conv.id}
                  className={`AdminConversationAdmin__chat-item ${
                    selectedChat?.id ===
                    conv.id
                      ? "AdminConversationAdmin__chat-item--active"
                      : ""
                  }`}
                  onClick={() =>
                    setSelectedChat(conv)
                  }
                >

                  {/* AVATAR */}

                  <div className="AdminConversationAdmin__chat-avatar">

                    <img
                      src={
                        conv.customer.avatar
                      }
                      alt={
                        conv.customer.name
                      }
                      onError={(e) => {
                        e.target.src =
                          "https://i.pravatar.cc/150";
                      }}
                    />

                    {conv.customer.online && (
                      <span className="AdminConversationAdmin__online-indicator"></span>
                    )}

                  </div>


                  {/* CHAT INFO */}

                  <div className="AdminConversationAdmin__chat-info">

                    <div className="AdminConversationAdmin__chat-header">

                      <span className="AdminConversationAdmin__chat-name">
                        {
                          conv.customer.name
                        }
                      </span>

                      <span className="AdminConversationAdmin__chat-time">
                        {
                          conv.lastActivity
                        }
                      </span>

                    </div>


                    <div className="AdminConversationAdmin__chat-order">

                      ORDER #
                      {conv.mlorder_id ??
                        conv.id}

                    </div>


                    <div className="AdminConversationAdmin__chat-preview">

                      {conv.preview ||
                        "No messages yet"}

                    </div>


                    <div className="AdminConversationAdmin__chat-badges">

                      {getStatusBadge(
                        conv.status
                      )}

                      {getPriorityBadge(
                        conv.priority
                      )}

                      {conv.category && (
                        <span className="AdminConversationAdmin__badge AdminConversationAdmin__badge--category">
                          {conv.category}
                        </span>
                      )}

                    </div>

                  </div>

                </div>

              )
            )}

          </div>

        </div>


        {/* ==============================================================
            CHAT AREA
        ============================================================== */}

        {selectedChat && (

          <div className="AdminConversationAdmin__chat-area">

            {/* ==========================================================
                CHAT HEADER
            ========================================================== */}

            <div className="AdminConversationAdmin__chat-header">

              <div className="AdminConversationAdmin__chat-header-left">

                <div className="AdminConversationAdmin__chat-avatar">

                  <img
                    src={
                      selectedChat
                        .customer
                        .avatar
                    }
                    alt={
                      selectedChat
                        .customer
                        .name
                    }
                    onError={(e) => {
                      e.target.src =
                        "https://i.pravatar.cc/150";
                    }}
                  />

                  {selectedChat
                    .customer
                    .online && (
                    <span className="AdminConversationAdmin__online-indicator"></span>
                  )}

                </div>


                <div>

                  <div className="AdminConversationAdmin__chat-header-name">

                    {
                      selectedChat
                        .customer
                        .name
                    }

                  </div>


                  <div className="AdminConversationAdmin__chat-header-meta">

                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />

                      <circle
                        cx="12"
                        cy="10"
                        r="3"
                      />
                    </svg>

                    {
                      selectedChat
                        .customer
                        .location ||
                      "Location not available"
                    }

                    {" • "}

                    Order #
                    {
                      selectedChat.mlorder_id ??
                      selectedChat.id
                    }

                  </div>

                </div>

              </div>


              {/* HEADER ACTIONS */}

              <div className="AdminConversationAdmin__chat-header-actions">

                <button
                  className="AdminConversationAdmin__btn AdminConversationAdmin__btn--secondary"
                  onClick={
                    handleViewOrder
                  }
                >
                  VIEW ORDER
                </button>


                <button
                  className="AdminConversationAdmin__btn AdminConversationAdmin__btn--primary"
                  onClick={
                    handleMarkResolved
                  }
                >
                  MARK RESOLVED
                </button>

              </div>

            </div>


            {/* ==========================================================
                MESSAGES
            ========================================================== */}

            <div className="AdminConversationAdmin__messages">

              {selectedChat.messages
                .map((message) => (

                  <div
                    key={message.id}
                    className={`AdminConversationAdmin__message ${
                      message.sender ===
                      "customer"
                        ? "AdminConversationAdmin__message--customer"
                        : message.sender ===
                          "bot"
                        ? "AdminConversationAdmin__message--assistant"
                        : "AdminConversationAdmin__message--agent"
                    }`}
                  >

                    {/* CUSTOMER AVATAR */}

                    {message.sender ===
                      "customer" && (

                      <img
                        src={
                          selectedChat
                            .customer
                            .avatar
                        }
                        alt=""
                        className="AdminConversationAdmin__message-avatar"
                        onError={(e) => {
                          e.target.src =
                            "https://i.pravatar.cc/150";
                        }}
                      />

                    )}


                    <div className="AdminConversationAdmin__message-content">

                      <div className="AdminConversationAdmin__message-bubble">

                        {message.text}

                      </div>


                      {/* BOT MESSAGE */}

                      {message.sender ===
                        "bot" && (

                        <div className="AdminConversationAdmin__message-meta">

                          ASSISTANT •{" "}

                          {
                            message.timestamp
                          }

                        </div>

                      )}


                      {/* CUSTOMER MESSAGE */}

                      {message.sender ===
                        "customer" && (

                        <div className="AdminConversationAdmin__message-time">

                          {
                            message.timestamp
                          }

                        </div>

                      )}


                      {/* ADMIN MESSAGE */}

                      {message.sender ===
                        "admin" && (

                        <div className="AdminConversationAdmin__message-time">

                          {
                            message.timestamp
                          }

                        </div>

                      )}

                    </div>

                  </div>

                ))}


              {/* HANDOVER */}

              {selectedChat.status ===
                "human_required" && (

                <div className="AdminConversationAdmin__handover-notice">

                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >

                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />

                    <circle
                      cx="9"
                      cy="7"
                      r="4"
                    />

                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />

                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />

                  </svg>

                  BOT HANDED OVER TO HUMAN AGENT

                </div>

              )}


              {/* TYPING */}

              {isTyping && (

                <div className="AdminConversationAdmin__typing-indicator">

                  <span className="AdminConversationAdmin__typing-dot"></span>

                  <span className="AdminConversationAdmin__typing-dot"></span>

                  <span className="AdminConversationAdmin__typing-dot"></span>

                  <span className="AdminConversationAdmin__typing-text">
                    TYPING...
                  </span>

                </div>

              )}


              <div ref={messagesEndRef} />

            </div>


            {/* ==========================================================
                MESSAGE INPUT
            ========================================================== */}

            <div className="AdminConversationAdmin__input-area">

              <input
                type="text"
                className="AdminConversationAdmin__message-input"
                placeholder="Type a response as an agent..."
                value={messageInput}
                onChange={(e) =>
                  setMessageInput(
                    e.target.value
                  )
                }
                onKeyDown={(e) => {

                  if (
                    e.key ===
                    "Enter"
                  ) {
                    e.preventDefault();
                    handleSendMessage();
                  }

                }}
              />


              <button
                className="AdminConversationAdmin__send-btn"
                onClick={
                  handleSendMessage
                }
              >

                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >

                  <line
                    x1="22"
                    y1="2"
                    x2="11"
                    y2="13"
                  />

                  <polygon points="22 2 15 22 11 13 2 9 22 2" />

                </svg>

              </button>


              <div className="AdminConversationAdmin__input-hint">

                <kbd>ENTER</kbd> TO SEND

              </div>

            </div>

          </div>

        )}

      </div>

    </div>
  );
};

export default Conversation;